import type { IPendingReview } from '~/domain/ShiftReview';
import type { TShiftCardData } from '~/types/ShiftCard';
import { computeHours, formatRate, parseCardDate } from '~/utils/shiftCard';

/**
 * A pending review asks for one of two things, and the card has to say which: hours the
 * professional submitted unchanged only need confirming, hours they adjusted need reviewing.
 *
 * Only the copy splits. The design system's `confirm-hours` status is deliberately excluded
 * from `DentalOfficeShiftCardStatus` (only `LargeShiftDetails` renders it) and is in any case
 * the visual twin of `review-hours` — same clock glyph, tone and text colour — so the card
 * stays in `review-hours` and overrides `statusLabel`.
 */
const CONFIRM_HOURS_LABEL = 'Confirm Hours';
const REVIEW_HOURS_LABEL = 'Review Hours';

/** Marshals a pending shift review into the DS shift card's `review-hours` state. */
export const mapPendingReviewToShiftCard = (review: IPendingReview): TShiftCardData => {
  const { shift, professional, offerOwner } = review;
  const isInterview = Boolean(review.isOnsiteInterview);

  const data: TShiftCardData = {
    status: 'review-hours',
    statusLabel: review.hasAdjustment ? REVIEW_HOURS_LABEL : CONFIRM_HOURS_LABEL,
    date: parseCardDate(shift.localDate),
    startTime: shift.localStartTime ?? '',
    endTime: shift.localEndTime,
    hours: computeHours(shift.localStartTime, shift.localEndTime),
    rate: isInterview ? undefined : formatRate(shift.rate),
    profession: review.profession,
    office: offerOwner.officeName ?? '',
    interview: isInterview,
  };

  if (professional.fullName || professional.image) {
    data.person = {
      ...(professional.fullName ? { name: professional.fullName } : {}),
      ...(professional.image ? { avatarUrl: professional.image } : {}),
    };
  }

  return data;
};
